import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Globe, ArrowLeft, Lock, CheckCircle, XCircle } from "lucide-react";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Invalid reset link. Please request a new password reset.");
    }
  }, [token]);

  // Use XMLHttpRequest to avoid body stream issues with external scripts
  const apiRequest = (url, options = {}) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || 'GET', url, true);
      if (options.headers) {
        Object.keys(options.headers).forEach(key => {
          xhr.setRequestHeader(key, options.headers[key]);
        });
      }
      xhr.timeout = 15000;
      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
        } catch (e) {
          resolve({ ok: false, status: xhr.status, data: { detail: 'Invalid server response' } });
        }
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.ontimeout = () => reject(new Error('Request timed out'));
      xhr.send(options.body || null);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const { ok, data } = await apiRequest(`${API}/auth/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          token,
          new_password: password 
        }),
      });

      if (ok) {
        setSuccess(true);
        toast.success("Password reset successfully!");
      } else {
        setError(data.detail || "Failed to reset password");
        toast.error(data.detail || "Failed to reset password");
      }
    } catch (err) {
      setError(err.message || "An error occurred. Please try again.");
      toast.error(err.message || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Globe className="w-10 h-10 text-slate-900" />
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Book an Interpreter</h1>
          </div>
          <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            {success ? "Password Reset!" : error && !token ? "Invalid Link" : "Set New Password"}
          </h2>
          <p className="text-slate-600 mt-2" style={{ fontFamily: 'Public Sans' }}>
            {success 
              ? "Your password has been changed successfully" 
              : error && !token
              ? "This reset link is invalid or has expired"
              : "Create a strong password for your account"}
          </p>
        </div>

        <div className="card p-8">
          {success ? (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <div className="space-y-2">
                <p className="text-slate-700">
                  Your password has been successfully reset.
                </p>
                <p className="text-sm text-slate-500">
                  You can now log in with your new password.
                </p>
              </div>
              <Link to="/login" className="block">
                <Button
                  className="w-full bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="go-to-login-btn"
                >
                  Go to Login
                </Button>
              </Link>
            </div>
          ) : error && !token ? (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <div className="space-y-2">
                <p className="text-slate-700">{error}</p>
              </div>
              <Link to="/forgot-password" className="block">
                <Button
                  className="w-full bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="request-new-link-btn"
                >
                  Request New Reset Link
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              
              <div>
                <Label htmlFor="password" className="text-slate-700 font-medium">New Password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    data-testid="reset-password-input"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-11 rounded-md border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    placeholder="••••••••"
                  />
                </div>
                <PasswordStrengthMeter password={password} />
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-slate-700 font-medium">Confirm New Password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    data-testid="reset-confirm-password-input"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 h-11 rounded-md border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    placeholder="••••••••"
                  />
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-sm text-red-600 mt-1">Passwords do not match</p>
                )}
                {confirmPassword && password === confirmPassword && (
                  <p className="text-sm text-emerald-600 mt-1">Passwords match</p>
                )}
              </div>

              <Button
                data-testid="reset-submit-btn"
                type="submit"
                disabled={loading || password !== confirmPassword}
                className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-md py-6 font-semibold shadow-sm hover:shadow-md transition-all disabled:opacity-50"
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
          )}

          {!success && token && (
            <div className="mt-6 text-center">
              <Link to="/login" className="text-slate-600 hover:text-slate-900 inline-flex items-center gap-2 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
            </div>
          )}
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-slate-600 hover:text-slate-900 transition-colors">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
